import type { ChainIdentifier } from "@opensea/graphql"
import type { Address, Item, ItemWithInitialPricing } from "../../types"

type TokenId = string
type ItemIdentifier = `${ChainIdentifier}-${Address}-${TokenId}`
export type PricingLookup = Record<
  ItemIdentifier,
  {
    unit: number
    contractAddress: Address
  }
>

/**
 * Converts an item to a string identifier.
 * @param chain - The chain identifier.
 * @param address - The token's address.
 * @param tokenId - The token's ID.
 * @returns The item's identifier.
 */
export const convertItemToIdentifier = (
  item: Pick<Item, "chain" | "contractAddress" | "tokenId">,
): ItemIdentifier =>
  `${item.chain.identifier}-${item.contractAddress}-${item.tokenId}`

/**
 * Builds a lookup of item identifiers to their prices.
 * @param items - The items to build the lookup for - must include pricing.
 * @returns The lookup of item identifiers to their prices.
 */

export const buildPricingLookup = (items: ItemWithInitialPricing[]) => {
  const lookup: PricingLookup = {}

  items.forEach(item => {
    const identifier = convertItemToIdentifier(item)
    if (item.initialPrice) {
      lookup[identifier] = item.initialPrice
    }
  })

  return lookup
}

/**
 * Retrieves the pricing for an item from the pricing lookup.
 * @param item - The item to retrieve the pricing for.
 * @param pricing - The pricing lookup.
 * @returns The pricing for the item.
 */
export const lookupPricingForItem = (
  item: Pick<Item, "chain" | "contractAddress" | "tokenId">,
  pricingLookup: PricingLookup | undefined,
) => {
  const identifier = convertItemToIdentifier(item)
  if (!pricingLookup) {
    return undefined
  }
  return pricingLookup[identifier]
}
